import apiClient, { type AxiosResponse } from "@shared/services/apiClient";

export interface ReportData {
  id: string;
  patientName: string;
  patientMRN: string;
  studyDate: string;
  reportDate: string;
  reportType: string;
  status: string;
  findings: string;
  impression: string;
  recommendation?: string;
  technician?: string;
  reviewer?: string;
}

export interface CreateReportRequest {
  eeg_study_id: string;
  eeg_recording_id?: string;
  report_type?: string;
  status?: "draft" | "pending_review" | "signed" | "amended";
  clinical_history?: string;
  indication?: string;
  medications?: string;
  technical_details?: string;
  findings?: string;
  impression?: string;
  recommendations?: string;
  /** Structured study-report extras (workflow, SCCIT, Guideline 7, sections) */
  structure?: Record<string, unknown> | null;
  workflow_kind?: string;
  sccit_terms?: string[];
  guideline7?: Record<string, unknown> | null;
}

export interface UpdateReportRequest {
  findings?: string;
  impression?: string;
  recommendations?: string;
  clinical_history?: string;
  medications?: string;
  reviewed_by?: string | null;
  status?: "draft" | "pending_review" | "signed" | "amended";
  structure?: Record<string, unknown> | null;
}

const prefix = "/reports";

const getById = async (id: string): Promise<AxiosResponse<ReportData>> => {
  return apiClient.get(`${prefix}/${id}`);
};

const create = async (
  data: CreateReportRequest,
): Promise<AxiosResponse<ReportData>> => {
  return apiClient.post(prefix, data);
};

const update = async (
  id: string,
  data: UpdateReportRequest,
): Promise<AxiosResponse<ReportData>> => {
  return apiClient.put(`${prefix}/${id}`, data);
};

const updateStatus = async (
  id: string,
  target_status: "draft" | "pending_review" | "signed" | "amended",
  options?: { reviewed_by?: string | null },
): Promise<AxiosResponse> => {
  return apiClient.patch(`${prefix}/${id}/status`, {
    target_status,
    ...(options?.reviewed_by ? { reviewed_by: options.reviewed_by } : {}),
  });
};

const recordTechReview = async (
  id: string,
  options?: { reviewerRole?: string; studyId?: string },
): Promise<AxiosResponse> =>
  apiClient.post(`${prefix}/${id}/tech-review`, {
    reviewerRole: options?.reviewerRole || "TECHNOLOGIST",
    ...(options?.studyId ? { studyId: options.studyId } : {}),
  });

const exportPDF = async (id: string): Promise<Blob> => {
  const response = await apiClient.get(`${prefix}/${id}/export`, {
    responseType: "blob",
  });
  return response.data;
};

const signReport = async (id: string): Promise<AxiosResponse> =>
  apiClient.patch(`${prefix}/${id}/sign`);

const getLatestSignature = async (id: string): Promise<AxiosResponse> =>
  apiClient.get(`${prefix}/${id}/signature`);

const getSignerCandidates = async (options?: {
  query?: string;
  reportId?: string;
  limit?: number;
}): Promise<AxiosResponse> =>
  apiClient.get(`${prefix}/signers`, {
    params: {
      ...(options?.query ? { q: options.query } : {}),
      ...(options?.reportId ? { report_id: options.reportId } : {}),
      ...(options?.limit ? { limit: options.limit } : {}),
    },
  });

const getMySignatureProfile = async (): Promise<AxiosResponse> =>
  apiClient.get("/users/me/signature-profile");

const uploadMySignatureImage = async (file: File): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append("signature", file);
  return apiClient.post("/users/me/signature-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

const deleteMySignatureImage = async (): Promise<AxiosResponse> =>
  apiClient.delete("/users/me/signature-image");

const getUserSignatureImageUrl = (userId: string, version?: string): string =>
  `${String(apiClient.defaults.baseURL || "").replace(/\/$/, "")}/users/${encodeURIComponent(userId)}/signature-image${
    version ? `?v=${encodeURIComponent(version)}` : ""
  }`;

const exportQeegPDF = async (id: string): Promise<Blob> => {
  const response = await apiClient.get(`${prefix}/${id}/export?format=qeeg`, {
    responseType: "blob",
  });
  return response.data;
};

const getBillingSuggestions = async (id: string): Promise<AxiosResponse> =>
  apiClient.get(`${prefix}/${id}/billing-suggestions`);

const saveBillingSummary = async (
  id: string,
  payload: { selected_icd10_codes: string[]; notes?: string },
): Promise<AxiosResponse> => apiClient.post(`${prefix}/${id}/billing-summary`, payload);

export default {
  getById,
  create,
  update,
  updateStatus,
  recordTechReview,
  exportPDF,
  signReport,
  getLatestSignature,
  getSignerCandidates,
  getMySignatureProfile,
  uploadMySignatureImage,
  deleteMySignatureImage,
  getUserSignatureImageUrl,
  exportQeegPDF,
  getBillingSuggestions,
  saveBillingSummary,
};
